import React, { useState, useEffect, useRef } from 'react';
import { getTask, updateTask, deleteTask, getTasks, addPredecessor, removePredecessor, updatePredecessor } from '../api';
import ConfirmDialog from './ConfirmDialog';
import TaskSOVLinks from './TaskSOVLinks';
import useConfirm from '../hooks/useConfirm';

/* ──────────────────────────────────────────────────────────────────
 * TaskEditModal — "Task Properties" popup
 *
 * Warm-cream + sage token redesign from the Claude Design handoff
 * `~/Downloads/design_handoff_task_properties` (2026-05-27).
 *
 * Behavior preserved verbatim from the prior slate-palette version:
 *  - Loads getTask + getTasks (skipped if availableTasks is passed in
 *    from ScheduleView's shared state).
 *  - Status ↔ Completion bidirectional coupling (drag slider/type a
 *    pct → status flips, and changing status flips pct to 0/50/100).
 *  - Milestone ↔ Task Type coupling (toggling Milestone sets Type to
 *    Milestone, and vice versa).
 *  - Predecessor add/edit/remove with inline picker that stays open
 *    after a successful add so the user can chain.
 *  - Dependent Tasks read-only roll-up (computed from allTasks).
 *  - SOV Links section delegated to <TaskSOVLinks/> below.
 *  - 2-step delete via shared ConfirmDialog.
 *  - Body scroll lock while open (prev value restored on unmount).
 *  - Double-submit guards on save/delete/predecessor add.
 *  - Save error surfaces as an inline danger banner above the footer.
 * ────────────────────────────────────────────────────────────────── */

const DEP_TYPES = [
  { value: 'FS', label: 'FS — Finish to Start', short: 'FS' },
  { value: 'SS', label: 'SS — Start to Start', short: 'SS' },
  { value: 'FF', label: 'FF — Finish to Finish', short: 'FF' },
  { value: 'SF', label: 'SF — Start to Finish', short: 'SF' },
];

const TASK_TYPES = ['Labor', 'Material', 'Procurement', 'Engineering', 'Inspection', 'Milestone'];

const TYPE_COLOR = {
  Labor:       'var(--type-labor)',
  Material:    'var(--type-material)',
  Procurement: 'var(--type-procurement)',
  Engineering: 'var(--type-engineering)',
  Inspection:  'var(--type-inspection)',
  Milestone:   'var(--type-milestone)',
};

const STATUS_META = {
  'Not Started': { color: 'var(--text-3)',      bg: 'var(--bg-surface-2)' },
  'In Progress': { color: 'var(--accent-text)', bg: 'var(--accent-soft)' },
  'Complete':    { color: 'var(--success)',     bg: 'var(--success-soft)' },
};

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const statusFromPct = (p) => {
  const n = Number(p) || 0;
  if (n >= 100) return 'Complete';
  if (n > 0) return 'In Progress';
  return 'Not Started';
};

/* ─────────────────────── Tiny stateless primitives ─────────────────────── */

const Pill = ({ color, bg, dot, children }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '2px 8px', borderRadius: 999,
    fontSize: 12.5, fontWeight: 500, color, background: bg,
    border: `1px solid color-mix(in oklch, ${color} 22%, transparent)`,
    letterSpacing: '-0.005em', whiteSpace: 'nowrap',
  }}>
    {dot && <span style={{ width: 5, height: 5, borderRadius: 99, background: color }} />}
    {children}
  </span>
);

const SectionHeader = ({ title, right, first = false }) => (
  <div style={{
    display: 'flex', alignItems: 'center',
    padding: first ? '4px 0 10px' : '22px 0 10px',
    borderBottom: '1px solid var(--border-subtle)',
    marginBottom: 12,
  }}>
    <h3 style={{
      margin: 0, fontSize: 12.5, fontWeight: 600,
      textTransform: 'uppercase', letterSpacing: '0.08em',
      color: 'var(--text-3)',
    }}>{title}</h3>
    <div style={{ flex: 1 }} />
    {right}
  </div>
);

const Label = ({ children }) => (
  <label style={{
    display: 'block', fontSize: 12, fontWeight: 500,
    color: 'var(--text-3)', textTransform: 'uppercase',
    letterSpacing: '0.05em', marginBottom: 6,
  }}>{children}</label>
);

const inputBaseStyle = {
  width: '100%', height: 34, padding: '0 10px',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)', borderRadius: 8,
  fontSize: 14.5, color: 'var(--text-1)',
  letterSpacing: '-0.005em',
  outline: 'none', boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const onFocusRing = (e) => {
  e.currentTarget.style.borderColor = 'var(--accent)';
  e.currentTarget.style.boxShadow = '0 0 0 2px var(--accent-ring)';
};
const onBlurRing = (e) => {
  e.currentTarget.style.borderColor = 'var(--border-default)';
  e.currentTarget.style.boxShadow = 'none';
};

/* ─────────────────────── Inline-SVG icons (no extra deps) ─────────────────────── */

const XIcon = ({ size = 16, sw = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
const TrashIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
  </svg>
);
const PlusIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const SearchIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
  </svg>
);
const ArrowRightIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h13M13 6l6 6-6 6" />
  </svg>
);
const LockIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const AlertCircleIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" />
  </svg>
);
const CheckIcon = ({ size = 11, sw = 2.4 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const Spinner = ({ size = 13, color = '#fff' }) => (
  <span style={{
    display: 'inline-block', width: size, height: size, borderRadius: 99,
    border: `2px solid color-mix(in oklch, ${color} 30%, transparent)`,
    borderTopColor: color,
    animation: 'spin .8s linear infinite',
    flexShrink: 0,
  }} />
);

/* ─────────────────────── Checkbox ─────────────────────── */

const Check = ({ checked, onChange, label, disabled }) => (
  <label style={{
    display: 'inline-flex', alignItems: 'center', gap: 8, position: 'relative',
    cursor: disabled ? 'not-allowed' : 'pointer', userSelect: 'none',
    opacity: disabled ? 0.6 : 1,
  }}>
    <input
      type="checkbox"
      checked={!!checked}
      onChange={onChange}
      disabled={disabled}
      style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
    />
    <span style={{
      width: 16, height: 16, borderRadius: 4,
      background: checked ? 'var(--accent)' : 'var(--bg-surface)',
      border: `1px solid ${checked ? 'var(--accent)' : 'var(--border-default)'}`,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', flexShrink: 0, transition: 'background .12s, border-color .12s',
    }}>
      {checked && <CheckIcon />}
    </span>
    <span style={{ fontSize: 14.5, color: 'var(--text-1)' }}>{label}</span>
  </label>
);

/* ─────────────────────── Completion slider ─────────────────────── */

const CompletionSlider = ({ value, onChange, disabled }) => {
  const [dragging, setDragging] = useState(false);
  const done = value === 100;
  const empty = value === 0;
  const trackColor = done ? 'var(--success)' : empty ? 'var(--border-default)' : 'var(--accent)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
      <div style={{ flex: 1, position: 'relative', height: 28, display: 'flex', alignItems: 'center' }}>
        <div style={{ position: 'relative', width: '100%', height: 6, background: 'var(--border-subtle)', borderRadius: 99 }}>
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0, width: `${value}%`,
            background: trackColor, borderRadius: 99,
            opacity: empty ? 0 : 1,
            transition: dragging ? 'none' : 'width .15s, background .12s',
          }} />
          <div style={{
            position: 'absolute', left: `calc(${value}% - 9px)`, top: '50%',
            transform: dragging ? 'translateY(-50%) scale(1.15)' : 'translateY(-50%)',
            width: 18, height: 18, borderRadius: 99,
            background: '#fff', border: `2px solid ${empty ? 'var(--border-strong)' : trackColor}`,
            boxShadow: dragging
              ? `0 0 0 6px color-mix(in oklch, ${trackColor} 18%, transparent), 0 2px 4px oklch(0.3 0.02 90 / 0.15)`
              : '0 1px 2px oklch(0.3 0.02 90 / 0.15)',
            transition: dragging ? 'none' : 'box-shadow .12s, transform .12s, border-color .12s',
            pointerEvents: 'none',
          }} />
        </div>
        <input
          type="range" min={0} max={100} step={1}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
          onMouseDown={() => setDragging(true)}
          onMouseUp={() => setDragging(false)}
          onTouchStart={() => setDragging(true)}
          onTouchEnd={() => setDragging(false)}
          onBlur={() => setDragging(false)}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            opacity: 0, cursor: disabled ? 'not-allowed' : 'pointer', margin: 0,
          }}
        />
      </div>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 2,
        height: 32, padding: '0 8px 0 10px',
        background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 7,
        fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em',
        fontSize: 14.5, color: empty ? 'var(--text-4)' : 'var(--text-1)', fontWeight: 500,
        minWidth: 70,
      }}>
        <input
          type="number" min={0} max={100} step={1}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(Math.min(100, Math.max(0, parseInt(e.target.value, 10) || 0)))}
          style={{
            flex: 1, width: '100%', minWidth: 0,
            background: 'transparent', border: 'none', outline: 'none',
            textAlign: 'right',
            fontFamily: 'inherit', letterSpacing: 'inherit', fontSize: 'inherit', fontWeight: 'inherit',
            color: 'inherit', padding: 0,
          }}
        />
        <span style={{ color: 'var(--text-3)', fontSize: 13.5 }}>%</span>
      </div>
    </div>
  );
};

const EmptyRow = ({ text }) => (
  <div style={{
    padding: '14px 12px',
    background: 'var(--bg-surface-2)',
    border: '1px dashed var(--border-default)',
    borderRadius: 8,
    fontSize: 14, color: 'var(--text-4)',
    marginBottom: 4,
  }}>{text}</div>
);

const ChipBtn = ({ onClick, disabled, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '5px 10px 5px 8px', borderRadius: 7,
      fontSize: 13.5, fontWeight: 500,
      color: 'var(--accent-text)',
      background: 'var(--accent-soft)',
      border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
    }}
  >
    <PlusIcon />{children}
  </button>
);

/* ─────────────────────── Main component ─────────────────────── */

const TaskEditModal = ({ taskId, projectId, onClose, onSaved, onDeleted, onAllocationsChanged, availableTasks }) => {
  const { confirm, confirmDialogProps } = useConfirm();
  const [task, setTask] = useState(null);
  const [_localAllTasks, _setLocalAllTasks] = useState([]);
  const allTasks = availableTasks && availableTasks.length ? availableTasks : _localAllTasks;
  const setAllTasks = _setLocalAllTasks;
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showAddPred, setShowAddPred] = useState(false);
  const [predSearch, setPredSearch] = useState('');
  const [addPredType, setAddPredType] = useState('FS');
  const [addPredLag, setAddPredLag] = useState(0);
  const [addingPred, setAddingPred] = useState(false);
  const predSearchRef = useRef(null);

  // Lock body scroll while open; restore on unmount.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Esc closes the modal (unless saving/deleting).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving && !deleting) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving, deleting]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [taskData, tasksData] = await Promise.all([
          getTask(taskId),
          availableTasks && availableTasks.length ? Promise.resolve(null) : getTasks(projectId),
        ]);
        setTask(taskData);
        if (tasksData) setAllTasks(tasksData);
        setFormData({
          task_name: taskData.task_name || '',
          task_description: taskData.task_description || '',
          building_area_phase: taskData.building_area_phase || '',
          task_type: taskData.task_type || 'Engineering',
          start_date: taskData.start_date || '',
          finish_date: taskData.finish_date || '',
          duration_days: taskData.duration_days || 0,
          percent_complete: taskData.percent_complete || 0,
          status: taskData.status || 'Not Started',
          milestone_flag: taskData.milestone_flag || 0,
          responsible_party: taskData.responsible_party || '',
          notes: taskData.notes || '',
          client_visible: taskData.client_visible ?? 1,
          task_code: taskData.task_code || '',
        });
      } catch (err) {
        console.error('Error loading task:', err);
      } finally {
        setLoading(false);
      }
    };
    if (taskId) load();
    // eslint-disable-next-line
  }, [taskId, projectId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value };
      // Milestone ↔ Task Type coupling
      if (name === 'milestone_flag') {
        if (checked) next.task_type = 'Milestone';
        else if (prev.task_type === 'Milestone') next.task_type = 'Engineering';
      }
      if (name === 'task_type') {
        next.milestone_flag = value === 'Milestone' ? 1 : 0;
      }
      return next;
    });
  };

  const setCompletion = (val) => {
    const pct = Math.min(100, Math.max(0, Number(val) || 0));
    setFormData(prev => ({ ...prev, percent_complete: pct, status: statusFromPct(pct) }));
  };
  const handleStatusChange = (e) => {
    const status = e.target.value;
    let pct = formData.percent_complete;
    if (status === 'Complete' && pct < 100) pct = 100;
    else if (status === 'Not Started' && pct > 0) pct = 0;
    else if (status === 'In Progress' && (pct === 0 || pct === 100)) pct = 50;
    setFormData(prev => ({ ...prev, status, percent_complete: pct }));
  };

  const editDuration = (() => {
    if (formData.start_date && formData.finish_date) {
      const s = new Date(formData.start_date);
      const f = new Date(formData.finish_date);
      return Math.max(1, Math.ceil((f - s) / (1000 * 60 * 60 * 24)) + 1);
    }
    return formData.duration_days || 0;
  })();

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const saveData = {
        task_name: formData.task_name,
        task_description: formData.task_description,
        building_area_phase: formData.building_area_phase,
        task_type: formData.task_type,
        start_date: formData.start_date,
        finish_date: formData.finish_date,
        duration_days: editDuration,
        percent_complete: parseFloat(formData.percent_complete) || 0,
        status: formData.status,
        milestone_flag: formData.milestone_flag ? 1 : 0,
        responsible_party: formData.responsible_party,
        notes: formData.notes,
        client_visible: formData.client_visible ? 1 : 0,
      };
      await updateTask(taskId, saveData);
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error('Error saving task:', err);
      setSaveError(err.message || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    const ok = await confirm({
      title: 'Delete Task',
      message: `Are you sure you want to delete "${formData.task_name}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      setDeleting(true);
      await deleteTask(taskId);
      if (onDeleted) onDeleted();
      onClose();
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Failed to delete task: ' + (err?.message || 'Unknown error'));
    } finally {
      setDeleting(false);
    }
  };

  const handlePredUpdate = async (predId, data) => {
    try {
      await updatePredecessor(taskId, predId, data);
      const updated = await getTask(taskId);
      setTask(updated);
    } catch (err) {
      console.error('Error updating predecessor:', err);
    }
  };

  const handleAddPredecessor = async (predecessorTaskId) => {
    if (addingPred) return;
    setAddingPred(true);
    try {
      await addPredecessor(taskId, {
        predecessor_task_id: predecessorTaskId,
        dependency_type: addPredType,
        lag_days: addPredLag,
      });
      const updated = await getTask(taskId);
      setTask(updated);
      setPredSearch('');
      setAddPredLag(0);
      setTimeout(() => predSearchRef.current?.focus(), 30);
    } catch (err) {
      console.error('Error adding predecessor:', err);
    } finally {
      setAddingPred(false);
    }
  };

  const handlePredRemove = async (predId) => {
    const ok = await confirm({
      title: 'Remove Predecessor',
      message: 'Remove this predecessor link?',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await removePredecessor(taskId, predId);
      const updated = await getTask(taskId);
      setTask(updated);
    } catch (err) {
      console.error('Error removing predecessor:', err);
    }
  };

  const pct = formData.percent_complete || 0;
  const predecessors = task?.predecessors || [];
  const successors = allTasks.filter(t =>
    (t.predecessors || []).some(p => Number(p.predecessor_task_id) === Number(taskId)),
  );
  const disable = saving || deleting;
  const liveStatus = formData.status || 'Not Started';
  const typeColor = TYPE_COLOR[formData.task_type] || 'var(--text-3)';

  return (
    <>
      <div
        onClick={!disable ? onClose : undefined}
        style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'oklch(0.3 0.02 90 / 0.42)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          paddingTop: 'max(28px, 5vh)',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 580, maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'calc(100vh - 56px)',
            display: 'flex', flexDirection: 'column',
            background: 'var(--bg-surface)',
            borderRadius: 16,
            border: '1px solid var(--border-subtle)',
            boxShadow: '0 20px 50px -10px oklch(0.2 0.02 90 / 0.25), 0 8px 20px oklch(0.2 0.02 90 / 0.08)',
            overflow: 'hidden',
          }}
        >
          {/* ─────────── Header ─────────── */}
          <div style={{
            padding: '18px 24px 16px',
            borderBottom: '1px solid var(--border-subtle)',
            background: 'var(--bg-surface)',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Pill
                color={typeColor}
                bg={`color-mix(in oklch, ${typeColor} 12%, transparent)`}
                dot
              >
                {formData.task_type || 'Engineering'}
              </Pill>
              {formData.task_code && (
                <span className="mono" style={{ fontSize: 13.5, color: 'var(--text-3)', fontWeight: 500 }}>
                  {formData.task_code}
                </span>
              )}
              <Pill
                color={STATUS_META[liveStatus]?.color || 'var(--text-3)'}
                bg={STATUS_META[liveStatus]?.bg || 'var(--bg-surface-2)'}
                dot
              >
                {liveStatus}
              </Pill>
              <div style={{ flex: 1 }} />
              <button
                type="button"
                onClick={onClose}
                disabled={disable}
                aria-label="Close"
                style={{
                  width: 30, height: 30, borderRadius: 7,
                  background: 'transparent', border: 'none',
                  color: 'var(--text-3)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  cursor: disable ? 'not-allowed' : 'pointer',
                  opacity: disable ? 0.4 : 1,
                }}
                onMouseEnter={(e) => { if (!disable) { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-1)'; } }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-3)'; }}
              >
                <XIcon />
              </button>
            </div>
            <h2 style={{
              margin: 0, fontSize: 19.5, fontWeight: 600,
              color: 'var(--text-1)', letterSpacing: '-0.018em', lineHeight: 1.3,
              wordBreak: 'break-word',
            }}>{formData.task_name || 'Task Properties'}</h2>
          </div>

          {/* ─────────── Body (scrollable) ─────────── */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 24px 8px' }}>
            {loading ? (
              <div style={{
                height: 360, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 12,
                color: 'var(--text-3)',
              }}>
                <Spinner size={22} color="var(--accent)" />
                <div style={{ fontSize: 14 }}>Loading task…</div>
              </div>
            ) : (
              <>
                {/* GENERAL */}
                <SectionHeader title="General" first />
                <div style={{ marginBottom: 12 }}>
                  <Label>Task Name</Label>
                  <input
                    type="text" name="task_name"
                    value={formData.task_name}
                    onChange={handleChange}
                    placeholder="Untitled task"
                    style={inputBaseStyle}
                    onFocus={onFocusRing} onBlur={onBlurRing}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <Label>Description</Label>
                  <textarea
                    name="task_description" rows={2}
                    value={formData.task_description}
                    onChange={handleChange}
                    placeholder="Describe the scope of this task…"
                    style={{
                      ...inputBaseStyle,
                      height: 'auto', minHeight: 56, padding: '8px 10px',
                      resize: 'vertical', lineHeight: 1.45,
                    }}
                    onFocus={onFocusRing} onBlur={onBlurRing}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <Label>Building Area / Phase</Label>
                    <input
                      type="text" name="building_area_phase"
                      value={formData.building_area_phase}
                      onChange={handleChange}
                      placeholder="e.g. Building A · Foundations"
                      style={inputBaseStyle}
                      onFocus={onFocusRing} onBlur={onBlurRing}
                    />
                  </div>
                  <div>
                    <Label>Task Type</Label>
                    <select
                      name="task_type"
                      value={formData.task_type}
                      onChange={handleChange}
                      style={{ ...inputBaseStyle, paddingRight: 28, cursor: 'pointer' }}
                      onFocus={onFocusRing} onBlur={onBlurRing}
                    >
                      {TASK_TYPES.map(tt => <option key={tt} value={tt}>{tt}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <Label>Status</Label>
                    <select
                      name="status"
                      value={formData.status || 'Not Started'}
                      onChange={handleStatusChange}
                      style={{ ...inputBaseStyle, paddingRight: 28, cursor: 'pointer' }}
                      onFocus={onFocusRing} onBlur={onBlurRing}
                    >
                      <option>Not Started</option>
                      <option>In Progress</option>
                      <option>Complete</option>
                    </select>
                  </div>
                  <div>
                    <Label>Responsible Party</Label>
                    <input
                      type="text" name="responsible_party"
                      value={formData.responsible_party}
                      onChange={handleChange}
                      placeholder="Crew or vendor"
                      style={inputBaseStyle}
                      onFocus={onFocusRing} onBlur={onBlurRing}
                    />
                  </div>
                </div>

                {/* SCHEDULE */}
                <SectionHeader title="Schedule" />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 110px', gap: 12, marginBottom: 12 }}>
                  <div>
                    <Label>Start Date</Label>
                    <input
                      type="date" name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                      style={{ ...inputBaseStyle, fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em' }}
                      onFocus={onFocusRing} onBlur={onBlurRing}
                    />
                  </div>
                  <div>
                    <Label>Finish Date</Label>
                    <input
                      type="date" name="finish_date"
                      value={formData.finish_date}
                      onChange={handleChange}
                      style={{ ...inputBaseStyle, fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em' }}
                      onFocus={onFocusRing} onBlur={onBlurRing}
                    />
                  </div>
                  <div>
                    <Label>Duration</Label>
                    <div style={{
                      ...inputBaseStyle,
                      display: 'flex', alignItems: 'center', gap: 6,
                      background: 'var(--bg-surface-2)',
                      border: '1px dashed var(--border-default)',
                      color: 'var(--text-3)',
                      fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em',
                    }}>
                      <span style={{ flex: 1 }}>{editDuration} days</span>
                      <LockIcon />
                    </div>
                  </div>
                </div>

                {/* COMPLETION */}
                <SectionHeader title="Completion" right={
                  liveStatus === 'In Progress' ? (
                    <span className="mono" style={{ fontSize: 12, color: 'var(--text-3)' }}>Auto-syncs to status</span>
                  ) : null
                } />
                <CompletionSlider value={pct} onChange={setCompletion} disabled={disable} />

                {/* PREDECESSORS */}
                <SectionHeader title="Predecessors" right={
                  <span className="mono" style={{ fontSize: 12.5, color: predecessors.length === 0 ? 'var(--text-4)' : 'var(--text-3)' }}>
                    {predecessors.length}
                  </span>
                } />
                {predecessors.length === 0 && !showAddPred && (
                  <EmptyRow text="No predecessors linked" />
                )}
                {predecessors.map(pred => {
                  const predTask = allTasks.find(tt => tt.id === pred.predecessor_task_id);
                  const lag = pred.lag_days || 0;
                  return (
                    <div key={pred.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 12px',
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 8,
                      marginBottom: 8,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
                          <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)' }}>{predTask?.task_code}</span>
                          <span style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{predTask?.task_name}</span>
                        </div>
                        {predTask && (predTask.start_date || predTask.finish_date) && (
                          <div className="mono" style={{ fontSize: 12, color: 'var(--text-4)' }}>
                            {predTask.start_date ? `Start: ${fmtDate(predTask.start_date)}` : ''}
                            {predTask.start_date && predTask.finish_date ? <span style={{ margin: '0 4px' }}>→</span> : null}
                            {predTask.finish_date ? `Finish: ${fmtDate(predTask.finish_date)}` : ''}
                          </div>
                        )}
                      </div>
                      <select
                        value={pred.dependency_type || 'FS'}
                        onChange={(e) => handlePredUpdate(pred.id, { dependency_type: e.target.value, lag_days: lag })}
                        style={{
                          height: 28, padding: '0 8px',
                          background: 'var(--bg-surface)',
                          border: '1px solid var(--border-default)', borderRadius: 6,
                          fontFamily: 'var(--font-mono)', fontSize: 13.5, fontWeight: 500, color: 'var(--text-1)',
                          cursor: 'pointer', outline: 'none',
                        }}
                        title="Dependency type"
                      >
                        {DEP_TYPES.map(d => <option key={d.value} value={d.value}>{d.short}</option>)}
                      </select>
                      <input
                        type="number"
                        value={lag}
                        onChange={(e) => handlePredUpdate(pred.id, { dependency_type: pred.dependency_type || 'FS', lag_days: parseInt(e.target.value, 10) || 0 })}
                        style={{
                          width: 56, height: 28, padding: '0 8px',
                          background: 'var(--bg-surface)',
                          border: '1px solid var(--border-default)', borderRadius: 6,
                          fontFamily: 'var(--font-mono)', fontSize: 13.5, color: lag === 0 ? 'var(--text-4)' : 'var(--text-1)',
                          textAlign: 'right', outline: 'none',
                        }}
                        title="Lag days"
                      />
                      <button
                        type="button"
                        onClick={() => handlePredRemove(pred.id)}
                        aria-label="Remove predecessor"
                        style={{
                          width: 24, height: 24, borderRadius: 5,
                          background: 'transparent', border: 'none', color: 'var(--danger)',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--danger-soft)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <XIcon size={13} />
                      </button>
                    </div>
                  );
                })}

                {showAddPred ? (
                  <div style={{
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 10,
                    padding: 12,
                    boxShadow: 'var(--shadow-sm)',
                    marginBottom: 4,
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '170px 110px 1fr', gap: 8, marginBottom: 10 }}>
                      <select
                        value={addPredType}
                        onChange={(e) => setAddPredType(e.target.value)}
                        style={{ ...inputBaseStyle, paddingRight: 28, cursor: 'pointer' }}
                        onFocus={onFocusRing} onBlur={onBlurRing}
                      >
                        {DEP_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                      </select>
                      <div style={{
                        display: 'flex', alignItems: 'center',
                        height: 34, padding: '0 10px',
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border-default)', borderRadius: 8,
                      }}>
                        <input
                          type="number"
                          value={addPredLag}
                          onChange={(e) => setAddPredLag(parseInt(e.target.value, 10) || 0)}
                          style={{
                            flex: 1, minWidth: 0, width: '100%',
                            background: 'transparent', border: 'none', outline: 'none',
                            fontFamily: 'var(--font-mono)', fontSize: 14.5, color: 'var(--text-1)',
                            letterSpacing: '-0.02em',
                          }}
                        />
                        <span style={{ color: 'var(--text-3)', fontSize: 12.5 }}>days lag</span>
                      </div>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        height: 34, padding: '0 10px',
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border-default)', borderRadius: 8,
                        color: 'var(--text-3)',
                      }}>
                        <SearchIcon />
                        <input
                          ref={predSearchRef}
                          type="text" autoFocus
                          value={predSearch}
                          onChange={(e) => setPredSearch(e.target.value)}
                          placeholder="Search tasks…"
                          style={{
                            flex: 1, minWidth: 0, height: '100%',
                            background: 'transparent', border: 'none', outline: 'none',
                            fontSize: 14.5, color: 'var(--text-1)',
                          }}
                          onFocus={(e) => { e.currentTarget.parentElement.style.borderColor = 'var(--accent)'; e.currentTarget.parentElement.style.boxShadow = '0 0 0 2px var(--accent-ring)'; }}
                          onBlur={(e) => { e.currentTarget.parentElement.style.borderColor = 'var(--border-default)'; e.currentTarget.parentElement.style.boxShadow = 'none'; }}
                        />
                      </div>
                    </div>
                    <div style={{
                      maxHeight: 160, overflowY: 'auto',
                      background: 'var(--bg-surface-2)',
                      border: '1px solid var(--border-subtle)', borderRadius: 8,
                    }}>
                      {(() => {
                        const existing = new Set(predecessors.map(p => p.predecessor_task_id));
                        const q = predSearch.toLowerCase().trim();
                        const candidates = allTasks.filter(ct =>
                          ct.id !== taskId &&
                          !existing.has(ct.id) &&
                          (q === '' || (ct.task_name || '').toLowerCase().includes(q) || (ct.task_code || '').toLowerCase().includes(q)),
                        ).slice(0, 30);
                        if (candidates.length === 0) {
                          return <div style={{ padding: '12px 14px', fontSize: 13.5, color: 'var(--text-4)', textAlign: 'center' }}>No matching tasks</div>;
                        }
                        return candidates.map((ct, i) => (
                          <div
                            key={ct.id}
                            onClick={() => !addingPred && handleAddPredecessor(ct.id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '8px 10px',
                              borderBottom: i < candidates.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                              cursor: addingPred ? 'wait' : 'pointer',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)', minWidth: 70 }}>{ct.task_code}</span>
                            <span style={{ fontSize: 14, color: 'var(--text-1)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ct.task_name}</span>
                            {ct.finish_date && (
                              <span className="mono" style={{ fontSize: 12, color: 'var(--text-4)' }}>{fmtDate(ct.finish_date)}</span>
                            )}
                          </div>
                        ));
                      })()}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                      <button
                        type="button"
                        onClick={() => { setShowAddPred(false); setPredSearch(''); }}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '0 14px', height: 32, borderRadius: 8,
                          fontSize: 14, fontWeight: 500, color: 'var(--text-2)',
                          background: 'var(--bg-surface)',
                          border: '1px solid var(--border-default)',
                          cursor: 'pointer',
                        }}
                      >Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: 4, marginBottom: 4 }}>
                    <ChipBtn onClick={() => setShowAddPred(true)} disabled={disable}>Add Predecessor</ChipBtn>
                  </div>
                )}

                {/* DEPENDENT TASKS */}
                <SectionHeader title="Dependent Tasks" right={
                  <span className="mono" style={{ fontSize: 12.5, color: successors.length === 0 ? 'var(--text-4)' : 'var(--text-3)' }}>
                    {successors.length}
                  </span>
                } />
                {successors.length === 0 ? (
                  <EmptyRow text="No tasks depend on this task" />
                ) : successors.map(s => {
                  const pred = (s.predecessors || []).find(p => Number(p.predecessor_task_id) === Number(taskId));
                  const depShort = DEP_TYPES.find(d => d.value === pred?.dependency_type)?.short || pred?.dependency_type || 'FS';
                  const lag = pred?.lag_days || 0;
                  return (
                    <div key={s.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 12px',
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 8,
                      marginBottom: 8,
                    }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: 6,
                        background: 'var(--accent-soft)',
                        color: 'var(--accent-text)',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <ArrowRightIcon />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
                          <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)' }}>{s.task_code}</span>
                          <span style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.task_name}</span>
                        </div>
                        {(s.start_date || s.finish_date) && (
                          <div className="mono" style={{ fontSize: 12, color: 'var(--text-4)' }}>
                            {s.start_date ? `Start: ${fmtDate(s.start_date)}` : ''}
                            {s.start_date && s.finish_date ? <span style={{ margin: '0 4px' }}>→</span> : null}
                            {s.finish_date ? `Finish: ${fmtDate(s.finish_date)}` : ''}
                          </div>
                        )}
                      </div>
                      <Pill color="var(--accent-text)" bg="var(--accent-soft)">
                        <span className="mono" style={{ letterSpacing: '-0.01em' }}>{depShort}</span>
                        {lag !== 0 && <span className="mono" style={{ opacity: 0.75 }}>{lag > 0 ? '+' : ''}{lag}d</span>}
                      </Pill>
                    </div>
                  );
                })}

                {/* OPTIONS */}
                <SectionHeader title="Options" />
                <div style={{ display: 'flex', gap: 28, marginBottom: 4 }}>
                  <Check
                    checked={!!formData.client_visible}
                    onChange={(e) => handleChange({ target: { name: 'client_visible', type: 'checkbox', checked: e.target.checked } })}
                    label="Client Visible"
                    disabled={disable}
                  />
                  <Check
                    checked={!!formData.milestone_flag}
                    onChange={(e) => handleChange({ target: { name: 'milestone_flag', type: 'checkbox', checked: e.target.checked } })}
                    label="Milestone"
                    disabled={disable}
                  />
                </div>

                {/* SOV LINKS */}
                <SectionHeader title="SOV Links" />
                <TaskSOVLinks
                  task={task}
                  projectId={projectId}
                  onAllocationsChanged={onAllocationsChanged}
                />

                {/* NOTES */}
                <SectionHeader title="Notes" />
                <textarea
                  name="notes" rows={3}
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Add notes for the field team or PM…"
                  style={{
                    ...inputBaseStyle,
                    height: 'auto', minHeight: 76, padding: '8px 10px',
                    resize: 'vertical', lineHeight: 1.45,
                  }}
                  onFocus={onFocusRing} onBlur={onBlurRing}
                />
                <div style={{ height: 8 }} />
              </>
            )}
          </div>

          {/* ─────────── Error banner ─────────── */}
          {saveError && (
            <div style={{
              margin: '0 14px',
              padding: '10px 12px 10px 16px',
              background: 'var(--danger-soft)',
              borderRadius: '8px 8px 0 0',
              boxShadow: 'inset 3px 0 0 var(--danger)',
              border: '1px solid color-mix(in oklch, var(--danger) 18%, transparent)',
              borderBottom: 'none',
              display: 'flex', alignItems: 'flex-start', gap: 8,
              position: 'relative', top: -1, marginBottom: -1,
              color: 'var(--danger)',
            }}>
              <AlertCircleIcon />
              <div style={{ flex: 1, fontSize: 14, lineHeight: 1.45 }}>
                <span style={{ fontWeight: 600 }}>Couldn't save changes.</span>
                <span style={{ display: 'block', marginTop: 1, opacity: 0.85 }}>{saveError}</span>
              </div>
              <button
                type="button"
                onClick={() => { setSaveError(null); handleSave(); }}
                style={{
                  padding: '2px 8px', borderRadius: 5,
                  fontSize: 13, fontWeight: 500, color: 'var(--danger)',
                  border: '1px solid color-mix(in oklch, var(--danger) 30%, transparent)',
                  background: 'var(--bg-surface)',
                  cursor: 'pointer',
                }}
              >Retry</button>
            </div>
          )}

          {/* ─────────── Footer ─────────── */}
          {!loading && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '14px 20px',
              borderTop: '1px solid var(--border-subtle)',
              background: 'var(--bg-surface-2)',
              flexShrink: 0,
            }}>
              <button
                type="button"
                onClick={handleDelete}
                disabled={disable && !deleting}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '0 14px', height: 36, borderRadius: 8,
                  fontSize: 14.5, fontWeight: 500,
                  color: 'var(--danger)',
                  background: 'var(--bg-surface)',
                  border: '1px solid color-mix(in oklch, var(--danger) 35%, transparent)',
                  cursor: deleting ? 'wait' : (saving ? 'not-allowed' : 'pointer'),
                  opacity: saving && !deleting ? 0.45 : 1,
                }}
                onMouseEnter={(e) => { if (!disable || deleting) e.currentTarget.style.background = 'var(--danger-soft)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-surface)'; }}
              >
                {deleting ? <><Spinner size={12} color="var(--danger)" /> Deleting…</> : <><TrashIcon /> Delete</>}
              </button>
              <div style={{ flex: 1 }} />
              <button
                type="button"
                onClick={onClose}
                disabled={disable}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '0 14px', height: 36, borderRadius: 8,
                  fontSize: 14.5, fontWeight: 500,
                  color: 'var(--text-2)',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-default)',
                  cursor: disable ? 'not-allowed' : 'pointer',
                  opacity: disable ? 0.45 : 1,
                }}
                onMouseEnter={(e) => { if (!disable) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-surface)'; }}
              >Cancel</button>
              <button
                type="button"
                onClick={handleSave}
                disabled={disable}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '0 16px', height: 36, borderRadius: 8,
                  fontSize: 14.5, fontWeight: 500, color: '#fff',
                  background: 'var(--accent)',
                  border: '1px solid color-mix(in oklch, var(--accent) 50%, black)',
                  boxShadow: '0 1px 0 color-mix(in oklch, var(--accent) 35%, black), 0 1px 3px oklch(0.3 0.05 135 / 0.18)',
                  cursor: saving ? 'wait' : (deleting ? 'not-allowed' : 'pointer'),
                  opacity: deleting ? 0.45 : 1,
                }}
                onMouseEnter={(e) => { if (!disable) e.currentTarget.style.background = 'var(--accent-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--accent)'; }}
              >
                {saving ? <><Spinner /> Saving…</> : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>
      <ConfirmDialog {...confirmDialogProps} />
    </>
  );
};

export default TaskEditModal;
